// File: src/components/Cards/ProductCard.tsx

import React, { useState, useEffect } from "react";
import { getProductRating } from "../../api";
import { ChevronDown24Regular, ChevronRight24Regular, List24Regular, Globe24Regular } from "@fluentui/react-icons";
import StarRating from "../StarsRating/StarRating";
import ReactMarkdown from "react-markdown";
import { toProductHref, linkTarget } from "../../utils/url";

import "./ProductCard.css";

interface ProductSpec {
    name: string;
    value: string;
}

interface Pricing {
    hasSalePrice: boolean;
    salePrice: number;
    listPrice: number;
    web?: {
        listPrice?: number;
        salePrice?: number;
        hasSalePrice?: boolean;
    };
    store?: {
        listPrice?: number;
        salePrice?: number;
        hasSalePrice?: boolean;
        netListPrice?: number;
        netSalePrice?: number;
    };
    priceSource?: "web" | "store";
}

interface ServiceItem {
    id: string;
    name: string;
    type: string;
    salePrice?: number;
    listPrice?: number;
    mandatory?: boolean;
    description?: string;
}

interface ServiceGroup {
    groupTitle: string;
    groupId: string;
    services: ServiceItem[];
}

interface Product {
    image_url?: string;
    sourcepage?: string;
    content?: string;
    sku?: string;
    sku_id?: string;
    brand?: string;
    pricing?: Pricing;
    specs?: ProductSpec[];
    benefits?: string | string[];
    price?: number;
    list_price?: number;
    store_list_price?: number;
    store_sale_price?: number;
    store_price?: number;
    storePrice?: number;
    price_source?: "web" | "store";
    services?: ServiceGroup[] | null;
}

interface ProductCardProps {
    product: Product;
    priceSource: "web" | "store";
}

const ProductCard: React.FC<ProductCardProps> = ({ product, priceSource }) => {
    const [showDetails, setShowDetails] = useState(false);
    const [showWebPrice, setShowWebPrice] = useState(false);
    const [rating, setRating] = useState<{ reviewCount: number; totalScore: number } | null>(null);
    const [selectedServiceIds, setSelectedServiceIds] = useState<Set<string>>(new Set());

    const { image_url, sourcepage, content = "Product", sku, sku_id, brand, pricing, specs = [], benefits, services } = product;
    const productSku = sku_id || sku;

    useEffect(() => {
        if (!productSku) return;

        const fetchRating = async () => {
            try {
                const data = await getProductRating(productSku);
                if (data) {
                    setRating(data);
                }
            } catch (error) {
                console.error("Error fetching product rating:", error);
            }
        };

        fetchRating();
    }, [productSku]);

    const formatPrice = (price: number) => new Intl.NumberFormat("el-GR", { style: "currency", currency: "EUR" }).format(price);
    const toNumber = (value: any): number | undefined => {
        if (typeof value === "number") return value;
        if (typeof value === "string" && value.trim()) {
            const parsed = Number(value);
            return isNaN(parsed) ? undefined : parsed;
        }
        return undefined;
    };

    const pricingData = (pricing ?? {}) as Pricing;
    const resolvedSource = priceSource || pricingData.priceSource || product.price_source || "web";
    const webPricing = pricingData.web || {
        listPrice: pricingData.listPrice,
        salePrice: pricingData.salePrice,
        hasSalePrice: pricingData.hasSalePrice
    };
    const storePricing = pricingData.store || {
        listPrice: product.store_list_price,
        salePrice: product.store_sale_price ?? product.store_price ?? product.storePrice,
        hasSalePrice: pricingData.hasSalePrice
    };

    const activeSource = resolvedSource === "store" ? "store" : "web";
    const activePricing = activeSource === "store" ? storePricing : webPricing;
    const activeListPrice = toNumber(activePricing.listPrice);
    const activeSalePrice = toNumber(activePricing.salePrice);
    const hasSalePrice = activePricing.hasSalePrice ?? (activeSalePrice !== undefined && activeListPrice !== undefined && activeSalePrice < activeListPrice);
    const displayPrice = hasSalePrice && activeSalePrice !== undefined ? activeSalePrice : activeListPrice ?? toNumber((product as any).price);
    const crossedPrice = hasSalePrice && activeListPrice ? activeListPrice : undefined;

    const resolvedWebPrice = toNumber(webPricing.salePrice) ?? toNumber(webPricing.listPrice);
    const resolvedStorePrice = toNumber(storePricing.salePrice) ?? toNumber(storePricing.listPrice);
    const showWebIndicator =
        activeSource === "store" && resolvedWebPrice !== undefined && resolvedStorePrice !== undefined && resolvedWebPrice < resolvedStorePrice;
    const showStoreComparison = activeSource === "web" && resolvedStorePrice !== undefined && displayPrice !== undefined && resolvedStorePrice > displayPrice;
    const shouldShowWebPriceButton = showWebIndicator;
    const showWebPriceDetails = shouldShowWebPriceButton && showWebPrice && resolvedWebPrice !== undefined;

    useEffect(() => {
        setShowWebPrice(false);
    }, [activeSource, resolvedWebPrice]);

    // Compute service stats
    const allServices = services?.flatMap(g => g.services) ?? [];
    const totalServiceCount = allServices.length;

    // Initialize selected services with preselected ones on first render
    useEffect(() => {
        if (allServices.length > 0 && selectedServiceIds.size === 0) {
            const preselected = new Set(allServices.filter(s => s.mandatory).map(s => s.id));
            if (preselected.size > 0) setSelectedServiceIds(preselected);
        }
    }, [services]);

    const selectedCost = allServices.filter(s => selectedServiceIds.has(s.id)).reduce((sum, s) => sum + (s.salePrice ?? 0), 0);
    const preselectedCost = allServices.filter(s => s.mandatory).reduce((sum, s) => sum + (s.salePrice ?? 0), 0);

    const toggleService = (id: string) => {
        setSelectedServiceIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const toggleDetails = () => {
        setShowDetails(!showDetails);
    };

    return (
        <div className={`product-card ${showDetails ? "expanded" : ""}`} onClick={toggleDetails}>
            <div className="product-header-actions">
                {shouldShowWebPriceButton && (
                    <button
                        className={`icon-button web-price-button ${showWebPrice ? "active" : ""}`}
                        onClick={e => {
                            e.stopPropagation();
                            setShowWebPrice(prev => !prev);
                        }}
                        title={showWebPrice ? "Hide web price" : "Show web price"}
                    >
                        <Globe24Regular />
                    </button>
                )}
            </div>

            {/* Main content wrapper for image, title, price etc */}
            <div className="product-main-content">
                {/* Left section with image and brand */}
                <div className="product-left">
                    <div className="product-image">
                        {image_url &&
                            sourcepage &&
                            (() => {
                                const href = toProductHref(sourcepage, productSku);
                                const target = linkTarget();
                                return (
                                    <a
                                        href={href}
                                        target={target}
                                        rel={target === "_blank" ? "noopener noreferrer" : undefined}
                                        onClick={e => e.stopPropagation()}
                                    >
                                        <img src={image_url} alt={content} />
                                    </a>
                                );
                            })()}
                    </div>
                    {brand && <div className="product-brand">{brand}</div>}
                </div>

                {/* Right section with details */}
                <div className="product-right">
                    {/* Title row */}
                    <div className="product-title-row">
                        <h3 className="product-title">
                            {sourcepage
                                ? (() => {
                                      const href = toProductHref(sourcepage, productSku);
                                      const target = linkTarget();
                                      return (
                                          <a
                                              href={href}
                                              target={target}
                                              rel={target === "_blank" ? "noopener noreferrer" : undefined}
                                              onClick={e => e.stopPropagation()}
                                          >
                                              {content}
                                          </a>
                                      );
                                  })()
                                : content}
                            {productSku && <span className="product-sku"> ({productSku})</span>}
                        </h3>
                    </div>

                    {/* Info row - rating left, price right */}
                    <div className="product-info-row">
                        {/* Rating - left aligned */}
                        <div className="product-rating">
                            {rating && rating.reviewCount > 0 ? (
                                <>
                                    <StarRating score={rating.totalScore} />
                                    <span className="review-count">
                                        ({rating.reviewCount} {rating.reviewCount === 1 ? "Αξιολόγηση" : "Αξιολογήσεις"})
                                    </span>
                                </>
                            ) : (
                                <span className="review-count">Χωρίς αξιολογήσεις</span>
                            )}
                        </div>

                        {/* Price - right aligned */}
                        <div className="product-price-section">
                            {crossedPrice !== undefined && <div className="product-original-price">{formatPrice(crossedPrice)}</div>}
                            <div className="product-price">{displayPrice !== undefined ? formatPrice(displayPrice) : "—"}</div>
                            {preselectedCost > 0 && (
                                <div className="product-preselected-cost-hint" data-copy-skip="true">
                                    + {formatPrice(preselectedCost)} προεπιλ. υπηρ.
                                </div>
                            )}
                            {showStoreComparison && resolvedStorePrice !== undefined && (
                                <div className="product-price-compare store">Τιμή Καταστήματος: {formatPrice(resolvedStorePrice)}</div>
                            )}
                            {showWebPriceDetails && resolvedWebPrice !== undefined && (
                                <div className="product-price-compare web">Τιμή Web: {formatPrice(resolvedWebPrice)}</div>
                            )}
                            {totalServiceCount > 0 && (
                                <div className="product-service-indicator" data-copy-skip="true">
                                    <span>
                                        {totalServiceCount} {totalServiceCount === 1 ? "υπηρεσία" : "υπηρεσίες"}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Benefits section - now at root level for full width with separator */}
            {benefits && (
                <div className="product-benefits">
                    {typeof benefits === "string" ? (
                        <ReactMarkdown>{benefits}</ReactMarkdown>
                    ) : (
                        Array.isArray(benefits) && benefits.map((benefit, idx) => <ReactMarkdown key={idx}>{benefit}</ReactMarkdown>)
                    )}
                </div>
            )}

            {/* Expandable content - specs and services */}
            <div className={`product-expandable ${showDetails ? "expanded" : ""}`}>
                {specs.length > 0 && (
                    <div className="product-specs-section">
                        <div className="specs-header">
                            <List24Regular className="specs-icon" />
                        </div>
                        <ul className="product-specs">
                            {specs.map((spec, index) => (
                                <li key={index}>
                                    <span className="spec-name">{spec.name}</span>
                                    <span className="spec-value">{spec.value}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                {services && services.length > 0 && (
                    <div className="product-services-section">
                        <div className="service-groups">
                            {services.map((group, gi) => (
                                <div key={gi} className="service-group">
                                    <div className="service-group-title">{group.groupTitle}</div>
                                    <ul className="service-list">
                                        {group.services.map((svc, si) => {
                                            const isSelected = selectedServiceIds.has(svc.id);

                                            return (
                                                <li key={si} className={`service-row ${isSelected ? "service-selected" : ""}`}>
                                                    <button
                                                        type="button"
                                                        className="service-name-col service-title-toggle"
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            toggleService(svc.id);
                                                        }}
                                                        aria-pressed={isSelected}
                                                    >
                                                        <span className="service-name">{svc.name}</span>
                                                        {svc.mandatory && <span className="service-preselected-badge">Προεπιλεγμένη</span>}
                                                    </button>
                                                    <span className="service-price-col">
                                                        <button
                                                            type="button"
                                                            className="service-price-button"
                                                            onClick={e => {
                                                                e.stopPropagation();
                                                                toggleService(svc.id);
                                                            }}
                                                            aria-pressed={isSelected}
                                                        >
                                                            <span className="service-price">
                                                                {svc.salePrice !== undefined ? formatPrice(svc.salePrice) : "—"}
                                                            </span>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={`service-toggle-button ${isSelected ? "selected" : ""}`}
                                                            onClick={e => {
                                                                e.stopPropagation();
                                                                toggleService(svc.id);
                                                            }}
                                                            aria-label={`${isSelected ? "Απενεργοποίηση" : "Επιλογή"} υπηρεσίας ${svc.name}`}
                                                            aria-pressed={isSelected}
                                                        >
                                                            <span className="service-toggle-switch" aria-hidden="true" />
                                                        </button>
                                                    </span>
                                                    {svc.description && <div className="service-description">{svc.description}</div>}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            ))}
                        </div>
                        {displayPrice !== undefined && (
                            <div className="service-total-row">
                                <span className="service-total-label">{selectedCost > 0 ? "Τελική τιμή με επιλεγμένες υπηρεσίες:" : "Τιμή προϊόντος:"}</span>
                                <span className="service-total-price">{formatPrice(displayPrice + selectedCost)}</span>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Expand indicator */}
            {(specs.length > 0 || (services && services.length > 0)) && (
                <div className="expand-indicator">{showDetails ? <ChevronDown24Regular /> : <ChevronRight24Regular />}</div>
            )}
        </div>
    );
};

export default React.memo(ProductCard);
